import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import { useCancelProcess } from '@/hooks/use-cancel-process'
import { useCancelExchangePreview } from '@/application/queries'
import type { CancelExchangePreview } from '@/domain/returns'

const {
  Callout,
  Button,
  Textarea,
  DialogTitle,
  DialogDescription,
  DialogCloseButton,
  DialogFooter
} = Base

const MIN_REASON_LENGTH = 5

interface CancelModalProps {
  onClose: () => void
  processId: string
  isExchangeOnly?: boolean
  hasExecutedStoreCreditRefund?: boolean
}

export const CancelModal: React.FC<CancelModalProps> = ({
  onClose,
  processId,
  isExchangeOnly = false,
  hasExecutedStoreCreditRefund = false
}) => {
  const { t } = useTranslation()
  const [cancelReason, setCancelReason] = useState('')
  const [reasonError, setReasonError] = useState(false)
  const {
    mutate: cancelProcess,
    isPending: loadingCancel,
    isSuccess: cancelSuccess
  } = useCancelProcess()
  const exchangePreview = useCancelExchangePreview(processId)

  const onCancelProcess = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (cancelReason.length < MIN_REASON_LENGTH) {
      setReasonError(true)
      return
    }

    cancelProcess({
      process_id: processId,
      reason: cancelReason
    })
  }

  return (
    <div className="w-[30rem]">
      <DialogCloseButton />
      <div className="space-y-3">
        <DialogTitle>{t('cancel_processes.modal_title')}</DialogTitle>
        <form className="space-y-6" onSubmit={onCancelProcess}>
          <div className="space-y-3">
            <Callout>
              <DialogDescription
                render={<ul />}
                className="m-0 list-disc space-y-1.5 pl-4"
              >
                <ExchangePreviewLine
                  {...exchangePreview}
                  isExchangeOnly={isExchangeOnly}
                />
                {hasExecutedStoreCreditRefund ? (
                  <li>{t('cancel_processes.store_credit_warning')}</li>
                ) : null}
                <li className="font-semibold">
                  {t('cancel_processes.modal_description_1')}
                </li>
                <li>{t('cancel_processes.modal_description_2')}</li>
              </DialogDescription>
            </Callout>
            <div className="space-y-1.5">
              <label className="inline-block text-sm" htmlFor="cancel-reason">
                {t('cancel_processes.modal_reason_label')}
              </label>
              <div className="flex flex-col gap-2">
                <Textarea
                  value={cancelReason}
                  onChange={(e) => {
                    setCancelReason(e.target.value)

                    if (e.currentTarget.value.length >= MIN_REASON_LENGTH) {
                      setReasonError(false)
                    }
                  }}
                  className="resize-none font-sans"
                  name="cancel-reason"
                  id="cancel-reason"
                />
                {reasonError ? (
                  <span className="pl-1 text-xs leading-none text-red-500">
                    {t('cancel_processes.modal_reason_validation')}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('cancel_processes.modal_dismiss_button')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={loadingCancel || cancelSuccess}
            >
              {t('cancel_processes.modal_confirm_button')}
            </Button>
          </DialogFooter>
        </form>
      </div>
    </div>
  )
}

interface ExchangePreviewLineProps {
  preview?: CancelExchangePreview
  isLoading: boolean
  isError: boolean
  isExchangeOnly: boolean
}

// On a preview error, fall back to the manual-cancel warning; the cancel itself is never blocked.
function ExchangePreviewLine({
  preview,
  isLoading,
  isError,
  isExchangeOnly
}: ExchangePreviewLineProps) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <li
        data-testid="exchange-preview-loading"
        className="h-4 w-3/4 animate-pulse rounded bg-neutral-200"
      />
    )
  }
  const outcome = isError
    ? errorFallbackOutcome(isExchangeOnly)
    : preview?.outcome
  switch (outcome) {
    case 'no_exchange':
    case undefined:
      return null
    case 'not_supported':
      return <li>{t('cancel_processes.exchange_order_warning')}</li>
    default:
      return (
        <li>
          <Trans
            i18nKey={`cancel_processes.exchange_preview.${outcome}`}
            values={{ order: preview?.exchangeOrderNumber }}
            components={{ b: <b /> }}
          />
        </li>
      )
  }
}

function errorFallbackOutcome(isExchangeOnly: boolean) {
  return isExchangeOnly ? 'not_supported' : 'no_exchange'
}
